import { Switch } from '@ads/components-react';
import type { MutableRefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PersonRolesViewModel } from '@/shared/api/generated/types';
import { Alert, Skeleton } from '@/shared/ui/atoms';
import { usePersonRoles } from '../../../../hooks/usePersonRoles';
import { useUpdatePersonRoles } from '../../../../hooks/useUpdatePersonRoles';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface RolesTabProps {
  readonly personId: number | undefined;
  /**
   * Ref exposta para que o `PersonForm` possa disparar o salvamento de vínculos
   * a partir da barra de navegação inferior, sem um botão extra no tab.
   */
  readonly saveRef?: MutableRefObject<(() => Promise<void>) | undefined>;
  /**
   * Callback chamado sempre que o estado de alterações não salvas muda.
   * `true` = há alterações pendentes; `false` = sem alterações.
   */
  readonly onDirtyChange?: (isDirty: boolean) => void;
}

// ─── Grupos de vínculos ───────────────────────────────────────────────────────

type RoleField = keyof Omit<PersonRolesViewModel, 'personId'>;

type RoleGroupKey = 'teaching' | 'content' | 'exams' | 'monograph' | 'sales' | 'other';

interface RoleGroup {
  readonly key: RoleGroupKey;
  readonly fields: readonly RoleField[];
}

const ROLE_GROUPS: readonly RoleGroup[] = [
  {
    key: 'teaching',
    fields: ['isStudent', 'isMonitor', 'isMonitorTCC', 'isEmployee', 'isTeachingStaff'],
  },
  {
    key: 'content',
    fields: ['isStreamer', 'isFreelancerMBX', 'isFreelancerEnglish', 'isReviewer'],
  },
  {
    key: 'exams',
    fields: ['isExamItemCreator', 'isExamItemApprover', 'seeOnlyOwnExamItem'],
  },
  {
    key: 'monograph',
    fields: [
      'isMonographCoordinator',
      'isMonographAdvisor',
      'isMonographOperational',
      'monographActive',
    ],
  },
  {
    key: 'sales',
    fields: ['isSeller', 'isSellerManager'],
  },
  {
    key: 'other',
    fields: [
      'isBoardMember',
      'isSocialAssistant',
      'isClassCoordinator',
      'isPolo',
      'isVisitor',
      'isTest',
    ],
  },
] as const;

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Aba de vínculos/papéis de uma pessoa. Consome `GET /api/v1/person/:id/roles`
 * e persiste alterações via `PUT /api/v1/person/:id/roles`.
 *
 * Os switches são agrupados por categoria. O salvamento é acionado pela barra
 * de navegação do `PersonForm` via `saveRef` — não há botão de salvar interno.
 *
 * @param personId - ID da pessoa. Quando `undefined`, exibe estado vazio.
 * @param saveRef  - Ref populada com a função async de salvamento, consumida pelo `PersonForm`.
 */
export function RolesTab({ personId, saveRef, onDirtyChange }: RolesTabProps) {
  const { t } = useTranslation('personManagement');

  const { data, isLoading, isError: isFetchError } = usePersonRoles(personId);
  const updateRoles = useUpdatePersonRoles(personId ?? 0);

  const [roles, setRoles] = useState<PersonRolesViewModel>({});

  // Sincroniza estado local quando os dados chegam da API.
  // Ref inicia como undefined (não como data) para garantir que o setRoles seja chamado
  // mesmo quando o TanStack Query retorna dados do cache imediatamente no primeiro render.
  const prevDataRef = useRef<PersonRolesViewModel | undefined>(undefined);
  if (prevDataRef.current !== data && data) {
    prevDataRef.current = data;
    setRoles(data);
  }

  // Mantém refs sempre atualizados para uso na função exposta via saveRef
  const rolesRef = useRef(roles);
  rolesRef.current = roles;
  const mutateAsyncRef = useRef(updateRoles.mutateAsync);
  mutateAsyncRef.current = updateRoles.mutateAsync;

  // Expõe função de salvamento para a barra de navegação do PersonForm
  if (saveRef) {
    saveRef.current = async () => {
      if (!personId) return;
      await mutateAsyncRef.current({ id: personId, data: rolesRef.current });
    };
  }

  // Detecta se há alterações pendentes comparando estado local com dados da API
  const isDirty = useMemo(() => {
    if (!data) return false;
    return (Object.keys(data) as Array<keyof PersonRolesViewModel>).some(
      key => key !== 'personId' && data[key] !== roles[key as RoleField],
    );
  }, [data, roles]);

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;

  useEffect(() => {
    onDirtyChangeRef.current?.(isDirty);
  }, [isDirty]);

  const toggle = (field: RoleField) => {
    setRoles(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (!personId) return null;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-xs">
        {Array.from({ length: 3 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholder
          <Skeleton key={i} className="h-10 w-full rounded-sm" />
        ))}
      </div>
    );
  }

  if (isFetchError) {
    return <Alert type="danger">{t('personForm.roles.fetchError')}</Alert>;
  }

  return (
    <div className="flex flex-col gap-xs">
      {ROLE_GROUPS.map(group => (
        <div
          key={group.key}
          className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border p-mini"
        >
          <h3 className="ts-heading-xs text-ctx-content-title">
            {t(`personForm.roles.groups.${group.key}`)}
          </h3>

          <div className="grid grid-cols-1 gap-mini @xl:grid-cols-2 @3xl:grid-cols-3">
            {group.fields.map(field => (
              <Switch
                key={field}
                size="md"
                label={t(`personForm.roles.fields.${field}`)}
                checked={roles[field] ?? false}
                onCheckedChange={() => toggle(field)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
